import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { CampusMails } from '@chancenwerk/db/CampusMails.js';
import { Registermethode, StudentType } from '@chancenwerk/db/enums/enums.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import {
  EntryInformation,
  SchoolInformation,
  Student,
} from '@chancenwerk/master-data-management/users/entities/Student.js';
import { KPFSecureAccessAPI } from '@chancenwerk/secure-access/api/KPFSecureAccessAPI.js';

@Entity()
export class CampusTokenUserData extends AdornisEntity {
  public static override _class = 'CampusTokenUserData';

  @validate(nonOptional({ translationKey: 'validation.firstName' }))
  @Field(type => String)
  public vorname!: string;

  @validate(nonOptional({ translationKey: 'validation.lastName' }))
  @Field(type => String)
  public nachname!: string;

  @validate<CampusTokenUserData>(({ value, key, source, step, ignoreMail }) => {
    if (ignoreMail) return;
    if (step) {
      if (!value)
        throw new ValidationError(`Bitte gebe deine E-Mail Adresse ein.`, {
          key,
          source,
          translationKey: 'errorStudentEmail',
        });
      if (value && !emailRegexCheck(value as string))
        throw new ValidationError(`${value as string} ist keine gültige E-Mail Adresse.`, {
          key,
          source,
          translationKey: 'errorStudentInvalidEmail',
        });
    }
  })
  @Field(type => String)
  public email!: string;

  @Field(type => Int)
  public grade!: number;

  @validate(nonOptional({ translationKey: 'validation.dataProtectionAccepted' }))
  @Field(type => Boolean)
  public dataProtectionAccepted!: boolean;

  @Field(type => String)
  public accessToken!: string;
}

const resolveRegisterStudentWithKPFCode = (userData: CampusTokenUserData) => {
  return async () => {
    const kpfCol = await getRawCollection<KPF>(KPF._collectionName);
    const kpfForToken = await KPFSecureAccessAPI.getByToken(userData.accessToken)({
      accessTokenForStudents: 1,
      tokenAccessCountMax: 1,
      tokenAccessCount: 1,
      ansprechPersonExtern: {
        email: 1,
      },
      kuerzel: 1,
      schoolID: 1,
      school: {
        kuerzel: 1,
      },
      produkt: {
        kuerzel: 1,
      },
    });

    if (
      !kpfForToken.accessTokenForStudents ||
      !kpfForToken.tokenAccessCountMax ||
      kpfForToken.tokenAccessCount === undefined
    ) {
      throw new Error('Token is unknown');
    }

    const newStudent = new Student({
      vorname: userData.vorname,
      nachname: userData.nachname,
      email: `dummy-${Math.floor(Math.random() * 1000000)}@chancenwerk.de`,
      schuelertyp: userData.grade <= 9 ? StudentType.US : StudentType.OS,
    });

    newStudent.joinInformation = new EntryInformation({
      registermethode: Registermethode.TOKEN,
      campusRegisterDataSend: true,
    });

    newStudent.schoolInformation = new SchoolInformation({
      jahrgangsstufe: userData.grade,
    });

    const student = await Student.createStudentWithKPF(
      newStudent,
      kpfForToken._id,
    )({
      _id: 1,
    });

    //await SemanticRelationship.saveSemanticRelationshipsAndUpdateRoles([rel])();

    kpfForToken.tokenAccessCount++;
    await kpfCol.updateOne({ _id: kpfForToken._id }, { $set: { tokenAccessCount: kpfForToken.tokenAccessCount } });

    if (kpfForToken.tokenAccessCount === Math.ceil(kpfForToken.tokenAccessCountMax * 0.9)) {
      await CampusMails.sendAccessTokenUsageInfoExternal(
        kpfForToken.tokenAccessCount,
        kpfForToken.tokenAccessCountMax,
        kpfForToken.accessTokenForStudents,
        '90',
        kpfForToken.kuerzel,
        kpfForToken.ansprechPersonExtern?.email,
      )();
      logger.info('90% of Token: ' + kpfForToken.accessTokenForStudents + ' are used');
    }

    if (Math.ceil(kpfForToken.tokenAccessCountMax * 1.15) <= kpfForToken.tokenAccessCount) {
      await CampusMails.sendAccessTokenUsageInfoInternal(
        kpfForToken.tokenAccessCount,
        kpfForToken.tokenAccessCountMax,
        kpfForToken.accessTokenForStudents,
        kpfForToken.kuerzel,
        kpfForToken._id,
        '115',
      )();
      logger.info('115% of Token: ' + kpfForToken.accessTokenForStudents + ' are used');
    }

    await Student.sendRegistrationMailForUserID(student._id)();
    return student._id;
  };
};

const registerStudentWithKPFCode = registerMutation({
  type: () => String,
  operationName: 'registerStudentWithKPFCode',
  params: [{ type: () => CampusTokenUserData, name: 'userData' }],
  resolve: resolveRegisterStudentWithKPFCode,
});

const resolveFinalizeVidis = (schoolyear: number) => {
  return async () => {
    const user = await CurrentUserInfo.getMyself<Student>()({
      roles: { name: 1, permissions: 1, contexts: 1 },
      email: 1,
    });

    if (!user || !(user.email.startsWith('vidisdummy+') && user.email.endsWith('@adornis.de'))) {
      throw new Error('User not found');
    }

    const col = await getRawCollection<Student>(Student._collectionName);
    await col.updateOne(
      { _id: user._id },
      {
        $set: {
          'schoolInformation.jahrgangsstufe': schoolyear,
          schuelertype: schoolyear < 8 ? StudentType.US : StudentType.OS,
        },
      },
    );

    return true;
  };
};

const finalizeVidisMutation = registerMutation({
  type: () => Boolean,
  operationName: 'finalizeVidis',
  params: [{ type: () => Int, name: 'schoolyear' }],
  resolve: resolveFinalizeVidis,
});

export namespace RegisterAPI {
  export const studentWithKPFCode = registerStudentWithKPFCode;
  export const finalizeVidis = finalizeVidisMutation;
}
